import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import Config from '../utils/Config';
import InlineVerificationCode from "./VerifyCodePanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useTitle from '../hooks/useTitle';
import { displayMessage } from '../utils/functions';
import Spinner from '../utils/spinner';
import "./Login.css";

const Login = () => {
  const { isAuthenticated, login, user } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const emailInputRef = useRef(null);
  const [messageClass, setMessageClass] = useState("warning hidden");
  const [showPassword, setShowPassword] = useState(false);
  const messageRef = useRef(null);
  const [timeoutMessage, setTimeoutMessage] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // MFA (mfaPending-based)
  const [loginComplete, setLoginComplete] = useState(false); // server says MFA needed
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [mfaChallengeId, setMfaChallengeId] = useState(null);

  // Lockout / rate limit display
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState(null);

  // UI helper messages
  const [pendingMessage, setPendingMessage] = useState("");
  const [pendingStyle, setPendingStyle] = useState("warning");

  useTitle('Login');

  useEffect(() => {
    if (localStorage.getItem("loggedOutDueToIdle") === "true") {
      setTimeoutMessage(true);
      localStorage.removeItem("loggedOutDueToIdle");
    }
  }, []);

  useEffect(() => {
    if (timeoutMessage && messageRef.current) {
      displayMessage(messageRef, "❌ You were logged out due to inactivity.", "warning", setMessageClass);
    }
  }, [timeoutMessage]);

  useEffect(() => {
    if (loginComplete && !codeVerified && pendingMessage && messageRef.current) {
      displayMessage(messageRef, pendingMessage, pendingStyle, setMessageClass);
    }
  }, [loginComplete, codeVerified, pendingMessage, pendingStyle]);

  useEffect(() => {
    if (isAuthenticated && (codeVerified || !loginComplete)) {
      const redirectPath = localStorage.getItem("postLoginRedirectPath");

      if (redirectPath) {
        localStorage.removeItem("postLoginRedirectPath");
        navigate(redirectPath);
      } else {
        navigate("/dashboard");
      }
    }
  }, [isAuthenticated, codeVerified, loginComplete, user, navigate]);

  const validateForm = () => {
    if (!email.trim()) {
      displayMessage(messageRef, "❌ Email is required.", "warning", setMessageClass);
      return false;
    }
    if (!password.trim()) {
      displayMessage(messageRef, "❌ Password is required.", "warning", setMessageClass);
      return false;
    }
    return true;
  };

  useEffect(() => {
    const storedRememberMe = localStorage.getItem("rememberMe") === "true";
    const storedEmail = localStorage.getItem("userEmail");
    if (storedRememberMe && storedEmail) {
      setEmail(storedEmail);
      setRememberMe(true);
    }
    emailInputRef.current?.focus();
  }, []);

  const onClose  = async (e) => {
    setLoginComplete(false);
  };

  const handleLogin = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validateForm()) return;

    setIsLoggingIn(true);
    try {
      const response = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, rememberMe }),
      });

      const data = await response.json();

      if (!response.ok) {
        displayMessage(messageRef, `❌ ${data.message || "Login failed."}`, "warning", setMessageClass);
        return;
      }

      // If MFA required, server returns challengeId + codeExpiresAt from mfaPending
      if (data.needsVerification) {
        setLoginComplete(true);
        setPendingMessage("✅ Verification code sent. Please check email.");
        setPendingStyle("success");
        if (data.challengeId) setMfaChallengeId(data.challengeId);
        if (data.codeExpiresAt) setCodeExpiresAt(data.codeExpiresAt);
        return;
      }

      // No MFA → proceed
      if (data.token) {
        login(data.token);
        localStorage.setItem("userEmail", email);
        localStorage.removeItem("claimId");
        if (rememberMe) {
          localStorage.setItem("authToken", data.token);
          localStorage.setItem("rememberMe", "true");
        } else {
          sessionStorage.setItem("authToken", data.token);
          localStorage.removeItem("rememberMe");
        }
        return;
      }

      displayMessage(messageRef, "❌ Unexpected server response.", "warning", setMessageClass);
    } catch (error) {
      displayMessage(messageRef, "❌ Error logging in.", "warning", setMessageClass);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleCodeSubmit = async (enteredCode) => {
    if (!mfaChallengeId) {
      displayMessage(messageRef, "❌ Challenge missing. Restart login.", "warning", setMessageClass);
      return;
    }
    try {
      const response = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_LOGIN_MFA_VERIFY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId: mfaChallengeId, code: enteredCode }),
      });

      const data = await response.json();

      if (response.status === 429) {
        const retryAfterSec = parseInt(response.headers.get('Retry-After') || '60', 10);
        const until = Date.now() + retryAfterSec * 1000;
        setIsLockedOut(true);
        setLockoutUntil(until);
        displayMessage(messageRef, data.message || "❌ Too many attempts. Try again later.", "warning", setMessageClass);
        setTimeout(() => setIsLockedOut(false), retryAfterSec * 1000);
        return;
      }

      if (response.ok && data.token) {
        setCodeVerified(true);
        login(data.token);
        localStorage.setItem("userEmail", email);
        localStorage.removeItem("claimId");
        if (rememberMe) {
          localStorage.setItem("authToken", data.token);
          localStorage.setItem("rememberMe", "true");
        } else {
          sessionStorage.setItem("authToken", data.token);
          localStorage.removeItem("rememberMe");
        }
      } else {
        displayMessage(messageRef, data.message || "Invalid or expired code.", "warning", setMessageClass);
      }
    } catch (err) {
      displayMessage(messageRef, "Server error. Try again later.", "warning", setMessageClass);
    }
  };

  const handleResend = async () => {
    if (!mfaChallengeId) {
      displayMessage(messageRef, "❌ Challenge missing. Restart login.", "warning", setMessageClass);
      return;
    }
    try {
      const r = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_LOGIN_MFA_RESEND}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId: mfaChallengeId }),
      });
      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        if (r.status === 429) {
          const ra = r.headers.get('Retry-After');
          if (ra) displayMessage(messageRef, `Too many requests. Try again in ${ra}s.`, "warning", setMessageClass);
        } else {
          displayMessage(messageRef, data?.message || "Could not resend code.", "warning", setMessageClass);
        }
        return;
      }

      if (data.codeExpiresAt) setCodeExpiresAt(data.codeExpiresAt);
      displayMessage(messageRef, "✅ Code resent. Check your email.", "success", setMessageClass);
    } catch {
      displayMessage(messageRef, "❌ Failed to resend code.", "warning", setMessageClass);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box" id="login-container">
        <h2>
          <span className="titleIcon"><FontAwesomeIcon icon="sign-in-alt" /></span>&nbsp;Log In
        </h2>
        <div ref={messageRef} className={messageClass} id="message" />

        {loginComplete && !codeVerified ? (
          <>
            <InlineVerificationCode
              onSubmit={handleCodeSubmit}
              onResend={handleResend}
              messageClass={messageClass}
              messageRef={messageRef}
              emailInputRef={emailInputRef}
              codeExpiresAt={codeExpiresAt}
              setExpiresAt={setCodeExpiresAt}
              setPendingMessage={setPendingMessage}
              setMessageClass={setMessageClass}
              setPendingStyle={setPendingStyle}
              email={email}
              parent="login"
              isLockedOut={isLockedOut}
              lockoutUntil={lockoutUntil}
              onClose={onClose}
              mfaChallengeId={mfaChallengeId}
            />
          </>
        ) : (
          <form onSubmit={handleLogin}>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
              ref={emailInputRef}
              disabled={isLoggingIn}
            />
            <div className="password-container">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoggingIn}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoggingIn}
              >
                {showPassword ? "🙈 Hide" : "👁️ Show"}
              </button>
            </div>

            <div className="login-options">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  disabled={isLoggingIn}
                />
                Remember Me
              </label>
              <span
                className="forgot-password"
                onClick={() => navigate("/forgot-password")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && navigate("/forgot-password")
                }
              >
                Forgot password?
              </span>
            </div>

            <p className="terms-text">
              By using this site, you agree to our{" "}
              <button
                type="button"
                className="link-button"
                onClick={() => navigate("/terms")}
                aria-label="Read Terms of Use"
              >
                Terms of Use
              </button>.
            </p>

            <button type="submit" className="login-button" disabled={isLoggingIn}>
              {isLoggingIn ? <span className="login-spinner"></span> : "Log In"}
            </button>
          </form>
        )}

        <div className="register-box">
          <p>Don't have an Account?</p>
          <button className="register-button" onClick={() => navigate("/register")}>
            Register
          </button>
        </div>
      </div>

      <Spinner show={isLoggingIn} />
    </div>
  );
};

export default Login;